import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import {
  DEFAULT_ROUTE_BY_ROLE,
  NAV_ITEMS,
  NavItem,
  ROUTE_PERMISSIONS,
  UserRole,
} from '../permissions';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  constructor(private authService: AuthService) {}

  getPermissions(): string[] {
    return this.authService.getPermissions();
  }

  hasPermission(permission: string): boolean {
    return this.getPermissions().includes(permission);
  }

  hasAnyPermission(permissions: string[]): boolean {
    const granted = this.getPermissions();
    return permissions.some((p) => granted.includes(p));
  }

  canAccessRoute(url: string): boolean {
    const path = this.normalizeUrl(url);
    const match = this.findRoutePermission(path);
    if (!match) {
      return true;
    }
    return this.hasPermission(match);
  }

  getPermissionForUrl(url: string): string | null {
    const path = this.normalizeUrl(url);
    return this.findRoutePermission(path);
  }

  getDefaultRoute(): string {
    const user = this.authService.getCurrentUser();
    if (!user) {
      return '/login';
    }
    const preferred = DEFAULT_ROUTE_BY_ROLE[user.role as UserRole] ?? '/billing';
    if (this.canAccessRoute(preferred)) {
      return preferred;
    }
    const first = ROUTE_PERMISSIONS.find((r) => this.hasPermission(r.permission));
    return first?.path ?? '/billing';
  }

  getNavItems(): NavItem[] {
    return NAV_ITEMS.filter((item) => this.hasPermission(item.permission));
  }

  isAdmin(): boolean {
    return this.authService.getCurrentUser()?.role === 'admin';
  }

  private findRoutePermission(path: string): string | null {
    const sorted = [...ROUTE_PERMISSIONS].sort((a, b) => b.path.length - a.path.length);
    for (const entry of sorted) {
      if (path === entry.path || path.startsWith(entry.path + '/')) {
        return entry.permission;
      }
    }
    return null;
  }

  private normalizeUrl(url: string): string {
    const withoutQuery = url.split('?')[0].split('#')[0];
    if (withoutQuery.length > 1 && withoutQuery.endsWith('/')) {
      return withoutQuery.slice(0, -1);
    }
    return withoutQuery || '/';
  }
}
